// tinker: tour — meet the seven neighbors. Two modes via config.mode:
//   freeplay → the whole piano is live over the drone; scale notes glow with
//              their degree numbers, non-scale keys are dimmed but playable.
//   personas → tap a degree chip 1–7 to hear that degree sustained over the
//              drone (~1.5s) and see its house-name from DEGREE_PERSONA.
// Always ready to continue. A "begin" tap resumes audio and starts the drone.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  HOME_MIDI,
  MAJOR_SCALE_SEMIS,
  DEGREE_PERSONA,
} from "../../../lib/homeKeyTheory";
import {
  PATCHES,
  playNote,
  startDrone,
  resumeAudio,
} from "../../../lib/homeKeyAudio";
import Piano from "../Piano";
import useHomeKeyVoices from "../useHomeKeyVoices";
import useTypingKeys from "../useTypingKeys";
import useHomeKeyMidi from "../useHomeKeyMidi";

const SUSTAIN_MS = 1500;
const DEGREES = [1, 2, 3, 4, 5, 6, 7];

// Sound the personas an octave above the G3 home so they sit clearly above
// the drone bed.
const midiForDegree = (degree) => HOME_MIDI + 12 + MAJOR_SCALE_SEMIS[degree - 1];

export default function TinkerTour({ moment, onReadyChange }) {
  const config = moment.config || {};
  const mode = config.mode || "freeplay";

  const [begun, setBegun] = useState(false);
  const [persona, setPersona] = useState(null); // degree currently introduced
  const [sustained, setSustained] = useState(null); // midi lit while it rings
  const droneRef = useRef(null);
  const sustainTimer = useRef(null);

  const voices = useHomeKeyVoices(PATCHES.softKeys);
  const { press, release, activeMidis } = voices;

  useEffect(() => {
    if (onReadyChange) onReadyChange(true);
  }, [onReadyChange]);

  const stopAll = useCallback(() => {
    if (droneRef.current) droneRef.current.stop();
    droneRef.current = null;
    if (sustainTimer.current) clearTimeout(sustainTimer.current);
    sustainTimer.current = null;
  }, []);
  useEffect(() => () => stopAll(), [stopAll]);

  useTypingKeys(press, release, begun);
  useHomeKeyMidi(press, release, begun);

  const begin = () => {
    resumeAudio();
    if (!droneRef.current) droneRef.current = startDrone();
    setBegun(true);
  };

  const tapDegree = (degree) => {
    resumeAudio();
    const midi = midiForDegree(degree);
    playNote(PATCHES.softKeys, midi, SUSTAIN_MS);
    setPersona(degree);
    setSustained(midi);
    if (sustainTimer.current) clearTimeout(sustainTimer.current);
    sustainTimer.current = setTimeout(() => setSustained(null), SUSTAIN_MS);
  };

  // Keys lit = whatever's being played, plus the persona note while it rings.
  const lit =
    sustained != null ? new Set([...activeMidis, sustained]) : activeMidis;

  const defaultPrompt =
    mode === "personas"
      ? "Tap a house number and listen to who lives there. Every neighbor greets the drone differently."
      : "The glowing keys are home's neighborhood — the G major scale — numbered by distance from home. The dim keys aren't forbidden, just outside it. Wander.";

  return (
    <div className="hk-moment-body">
      <p>{config.prompt || defaultPrompt}</p>

      {!begun && (
        <button type="button" className="hk-btn hk-btn--primary" onClick={begin}>
          begin — fade the drone in
        </button>
      )}

      {begun && mode === "personas" && (
        <>
          <div className="hk-choice-row" role="group" aria-label="scale degrees">
            {DEGREES.map((degree) => (
              <button
                key={degree}
                type="button"
                className={`hk-choice${persona === degree ? " hk-choice--picked" : ""}`}
                aria-pressed={persona === degree}
                onClick={() => tapDegree(degree)}
              >
                {degree}
              </button>
            ))}
          </div>
          {persona != null && (
            <p className="hk-persona" aria-live="polite">
              <strong>{persona}</strong> — {DEGREE_PERSONA[persona]}
            </p>
          )}
        </>
      )}

      <Piano
        activeMidis={lit}
        onPress={press}
        onRelease={release}
        highlight={{ scaleOnly: true }}
        disabled={!begun}
      />
    </div>
  );
}
